import { useState } from "react";
import { DEFAULT_SEGMENT_NOUN, type SegmentNoun } from "@keydials/dial-workspace";
import { api, getAccessToken } from "../auth/AuthContext";

/** The slice of the loaded dial these switches merge back optimistically. */
type DisplayPatch = { hideEmptyEnabled?: boolean; hideEmptyForMe?: boolean; sectionEdgeSplitEnabled?: boolean; miloHidden?: boolean };

/**
 * Dial display settings — how this dial reads for everyone on it, rather than how any one person has
 * chosen to view it. Two switches: whether the dial offers the "hide empty action points" filter
 * (KEY-457), and whether its section status edges are split per action point (KEY-494).
 *
 * Both are dial decisions rather than personal ones because they change what everyone is offered or
 * shown. Whether a dial is the kind where an empty action point means "not applicable here, get it out
 * of my way" or "nobody has filled this in yet, and that is exactly what I need to see" is a judgement
 * about the dial. Before KEY-457, the filter simply appeared wherever a dial happened to contain an
 * empty node.
 */
export function DisplaySection({
  instanceId,
  hideEmptyEnabled,
  hideEmptyForMe,
  sectionEdgeSplitEnabled,
  miloEnabled = false,
  miloHidden = false,
  listTabLabel,
  canHideAnything,
  onApplied,
  noun = DEFAULT_SEGMENT_NOUN,
}: {
  instanceId: string;
  hideEmptyEnabled: boolean;
  /** This person's own filter state (KEY-547) — needed to put it back if the write below is refused,
   * and to say truthfully whether it is on for them right now. */
  hideEmptyForMe: boolean;
  sectionEdgeSplitEnabled: boolean;
  /** KEY-715: whether this dial's definition offers Milo at all. The Milo card is shown only when it does — there is
   * nothing to switch on a dial that was never offered him. */
  miloEnabled?: boolean;
  /** KEY-715: whether somebody on the dial said "no thanks" to Milo's introduction, for the whole household. */
  miloHidden?: boolean;
  /** What the dial's list tab is called here — "Subdials" on a group, "Sections" everywhere else
   * (KEY-529). The copy names it so the reader can go and find the switch. */
  listTabLabel: string;
  /** Whether the filter could hide anything on this dial as it stands. False on a simple dial, and on a
   * dial with nothing empty or nothing BUT empty — in all of which the switch never appears, so the card
   * has to say why rather than leave the setting looking broken. */
  canHideAnything: boolean;
  /** KEY-523: merge the flag into the loaded dial. Each switch is one boolean the server writes and
   * nothing else, so refetching the dial to read it back is five requests spent learning what we sent. */
  onApplied: (patch: DisplayPatch) => void;
  /** This dial's word for an action point (KEY-539); omitted means the platform's word. */
  noun?: SegmentNoun;
}) {
  const [busy, setBusy] = useState(false);
  // Which switch failed, not merely that one did: the two cards used to share a single message, so a
  // refusal on one of them printed under both.
  const [failed, setFailed] = useState<"hideEmpty" | "split" | "milo" | null>(null);

  // Optimistic, like the status writes in Workspace: the dial answers on the click and a refusal puts the
  // old value back. `applied` is the value to merge on success, `prior` the one to restore on failure.
  async function run(
    which: "hideEmpty" | "split" | "milo",
    change: (token: string) => Promise<void>,
    applied: DisplayPatch,
    prior: DisplayPatch,
  ) {
    const token = getAccessToken();
    if (!token) return;
    setBusy(true);
    setFailed(null);
    onApplied(applied);
    try {
      await change(token);
    } catch {
      onApplied(prior);
      setFailed(which);
    } finally {
      setBusy(false);
    }
  }

  // KEY-547. Turning this on also turns the filter on for the person who flipped it, because from where
  // they are standing there is nothing else to see: the Settings tab takes the dial column away entirely,
  // so the switch this setting reveals — and the banner that announces it — are both off screen at the
  // moment of the click. The setting read as inert, and was reported as a bug on exactly those grounds.
  //
  // Still two writes, not one merged control: `hideEmptyEnabled` is one instance-wide decision about what
  // everyone is OFFERED, `hideEmptyForMe` is per-user state about what one person is DOING. Collapsing
  // them would let one viewer's preference change what every other viewer sees.
  //
  // Sequential rather than parallel: the per-user endpoint needs only read access, so a refused enable
  // must not leave a stray preference written behind it. Off→on only — turning the switch OFF writes
  // nothing per-user, because the server withholds the flag while the instance setting is off and the
  // preference row is kept deliberately, so switching back on restores what people had.
  const toggle = (next: boolean) =>
    run("hideEmpty", async (token) => {
      await api.setInstanceHideEmpty(token, instanceId, next);
      if (next) {
        await api.setHideEmptyForMe(token, instanceId, true);
      }
    },
      next ? { hideEmptyEnabled: true, hideEmptyForMe: true } : { hideEmptyEnabled: false },
      next ? { hideEmptyEnabled: hideEmptyEnabled, hideEmptyForMe: hideEmptyForMe } : { hideEmptyEnabled: hideEmptyEnabled });
  const toggleSplit = (next: boolean) =>
    run("split", (token) => api.setInstanceSectionEdgeSplit(token, instanceId, next),
      { sectionEdgeSplitEnabled: next }, { sectionEdgeSplitEnabled: sectionEdgeSplitEnabled });
  // KEY-715: the switch reads "show Milo", so checked means NOT hidden.
  const toggleMilo = (show: boolean) =>
    run("milo", (token) => api.setInstanceMiloHidden(token, instanceId, !show),
      { miloHidden: !show }, { miloHidden: miloHidden });

  const refusal = (which: "hideEmpty" | "split" | "milo") =>
    failed === which
      ? <div role="alert" style={{ marginTop: 10, fontSize: 13, color: "var(--st-overdue)" }}>
          Couldn't change that. You may not have permission on this dial.
        </div>
      : null;

  return (
    <>
      <div className="kd-card" style={{ padding: "var(--pad-card)" }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>Hiding empty {noun.many}</div>
        <p style={{ margin: "8px 0 0", fontSize: 13.5, lineHeight: 1.6, color: "var(--text-muted)" }}>
          When this is on, anyone looking at this dial can choose to hide {noun.many} that have nothing in
          them yet. Their choice is their own — it doesn't change what anybody else sees — and the dial says
          so above itself whenever something is being hidden.
        </p>
        {/* KEY-547: the setting and the choice it unlocks are two controls on two screens, and this screen
            hides the dial entirely while it is open — so whoever turns this on cannot see the thing they
            just turned on. Say where it is. */}
        <p style={{ margin: "8px 0 0", fontSize: 13.5, lineHeight: 1.6, color: "var(--text-muted)" }}>
          Each person makes that choice for themselves with the <strong>Hide empty</strong> switch beside
          the dial's {listTabLabel} tab
          {hideEmptyEnabled && hideEmptyForMe ? " — which is switched on for you now." : "."}
        </p>
        {!canHideAnything && (
          <p style={{ margin: "8px 0 0", fontSize: 13, lineHeight: 1.6, color: "var(--text-faint)" }}>
            There is nothing for it to hide on this dial as it stands, so the switch isn't being offered
            beside the {listTabLabel} tab. It appears by itself once the dial has a mix — some {noun.many}{" "}
            with things in them and some without.
          </p>
        )}
        <p style={{ margin: "8px 0 0", fontSize: 13, lineHeight: 1.6, color: "var(--text-faint)" }}>
          Leave it off for a dial where an empty {noun.one} is the point — somewhere "nobody has done this
          yet" is the thing you need to see.
        </p>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 9, marginTop: 14, cursor: busy ? "wait" : "pointer" }}>
          <input
            type="checkbox"
            checked={hideEmptyEnabled}
            disabled={busy}
            onChange={(e) => void toggle(e.target.checked)}
          />
          <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)" }}>
            Let people hide empty {noun.many}
          </span>
        </label>
        {refusal("hideEmpty")}
      </div>

      <div className="kd-card" style={{ padding: "var(--pad-card)" }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>The ring around each section</div>
        <p style={{ margin: "8px 0 0", fontSize: 13.5, lineHeight: 1.6, color: "var(--text-muted)" }}>
          A section's status is drawn as a coloured line around the outside of it. Normally that line is one
          colour: the worst status anything in the section sits at. Switch this on and the line is cut into
          one piece per {noun.one}, each showing that {noun.one}'s own worst — so you can see which part
          of the section is dragging it down without opening it.
        </p>
        <p style={{ margin: "8px 0 0", fontSize: 13, lineHeight: 1.6, color: "var(--text-faint)" }}>
          Both readings use the same check, so the colours mean the same thing either way. A section whose
          status somebody has set by hand keeps its single line — that is a person's judgement about the
          whole section, not something to split up.
        </p>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 9, marginTop: 14, cursor: busy ? "wait" : "pointer" }}>
          <input
            type="checkbox"
            checked={sectionEdgeSplitEnabled}
            disabled={busy}
            onChange={(e) => void toggleSplit(e.target.checked)}
          />
          <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)" }}>
            Split the section ring by {noun.one}
          </span>
        </label>
        {refusal("split")}
      </div>
      {miloEnabled && (
        <div className="kd-card" style={{ padding: "var(--pad-card)" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>Milo, the assistant</div>
          <p style={{ margin: "8px 0 0", fontSize: 13.5, lineHeight: 1.6, color: "var(--text-muted)" }}>
            Milo is offered on this dial. If somebody on it said "no thanks" to his introduction, the Milo button is
            put away for everyone here — this switch brings him back. Switching him off here does the same as that
            "no thanks".
          </p>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 9, marginTop: 14, cursor: busy ? "wait" : "pointer" }}>
            <input
              type="checkbox"
              checked={!miloHidden}
              disabled={busy}
              onChange={(e) => void toggleMilo(e.target.checked)}
            />
            <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)" }}>
              Show Milo on this dial
            </span>
          </label>
          {refusal("milo")}
        </div>
      )}
    </>
  );
}
